import { observable, action, makeObservable } from "mobx";
import type RootStore from "@stores/RootStore";
import { AxiosError } from "axios";
import AuthenticationService from "../services/AuthenticationService";
import LoginForm from "../forms/LoginForm";
import BaseFormStore from "@components/common/form/stores/BaseFormStore";
import AuthenticationModel from "@api/models/AuthenticationModel";
import { deleteLocalStorageItem, setLocalStorageItem } from "@utils/localStorageUtil";
import { accessTokenKey, refreshTokenKey } from "@const/localStorage";

export default class AuthenticationStore extends BaseFormStore {
    rootStore: RootStore;
    service: AuthenticationService;
    form: LoginForm;

    constructor(rootStore: RootStore) {
        super({
            fnGet: async (itemId) => {
                //nista
            }
        });

        this.rootStore = rootStore;
        this.service = new AuthenticationService();

        this.form = new LoginForm(
            {
                onSuccess: () => {
                    this.onFormSuccess();
                },
                onError() {
                    // Trenutno ne radimo ništa na onError jer LoginForm handle-a greške interno
                }
            },
            {},
            rootStore
        );

        makeObservable(this, {
            //#region UI
            isLoading: observable,
            setIsLoading: action,
            isLoginDrawerOpen: observable,
            setIsLoginDrawerOpen: action,
            //#endregion

            onFormSuccess: action,
            loginAsync: action,
            logoutAsync: action,
        });
    }

    //#region UI

    isLoading = false;
    setIsLoading = (value: boolean) => {
        this.isLoading = value;
    };

    isLoginDrawerOpen = false;
    setIsLoginDrawerOpen = (value: boolean) => {
        this.isLoginDrawerOpen = value;
    };

    //#endregion

    onFormSuccess = async () => {
        const values = this.form.values();
        if (this.isCreateNewMode) {
            await this.loginAsync(values);
        }
    };

    loginAsync = async (authenticationModel: AuthenticationModel) => {
        try {
            this.setIsLoading(true);

            const response = await this.service.loginAsync(authenticationModel.userName, authenticationModel.password);

            if (response.status === 200) {
                setLocalStorageItem(accessTokenKey, response.data.access_token);

                if (response.data.refresh_token) {
                    setLocalStorageItem(refreshTokenKey, response.data.refresh_token);
                }

                await this.rootStore.authorizationStore.authorizeUserAsync();
                this.setIsLoginDrawerOpen(false);
            }
        } catch (ex) {
            if (ex instanceof AxiosError) {
                if (ex.response?.status === 400) {
                    this.rootStore.toastStore.alert(
                        "error",
                        "Pogrešno korisničko ime ili lozinka.",
                        "Provjeri jesi li unijeo ispravne podatke."
                    );
                } else {
                    this.rootStore.toastStore.alert(
                        "error",
                        "Dogodila se neočekivana greška.",
                        "Molimo pokušaj ponovo."
                    );
                }
            }
        } finally {
            this.setIsLoading(false);
        }
    };

    logoutAsync = () => {
        deleteLocalStorageItem(accessTokenKey);
        deleteLocalStorageItem(refreshTokenKey);
        this.rootStore.authorizationStore.setUser(null);
        this.rootStore.routerStore.navigate("/");
    };
}